/* eslint-disable @typescript-eslint/explicit-module-boundary-types */

import { formatDate, formatNumber } from "$root/localization/formatters";
import { builtInCaseInsensitiveSort } from "$root/utils";
import { type KeyOrPathArray, accessByPath, pathToStr } from "$root/utils/objects";
import type { TableColumn, TableWithGroupsColumn } from "@mdotm/shadcn/legacy/components";
import { type SortFn, builtInSort } from "@mdotm/shadcn/legacy/utils";

function builtInSortFnForPath<T>(path: KeyOrPathArray<T>, direction?: "asc" | "desc"): SortFn<T> {
	return (rowA, rowB) => builtInSort(accessByPath(rowA, path), accessByPath(rowB, path), direction);
}

export function builtInCaseInsensitiveSortFnForPath<T>(path: KeyOrPathArray<T>, direction?: "asc" | "desc"): SortFn<T> {
	return (rowA, rowB) =>
		builtInCaseInsensitiveSort(String(accessByPath(rowA, path)), String(accessByPath(rowB, path)), direction);
}

export const commonColumns = {
	string<T>(
		path: KeyOrPathArray<T>,
	): Pick<TableColumn<T, string>, "name" | "cellClassName" | "align" | "content" | "sortFn"> {
		return {
			name: pathToStr(path),
			content: (row) => {
				const val = accessByPath(row, path);
				return val == null || typeof val !== "string" ? null : String(val);
			},
			sortFn: builtInCaseInsensitiveSortFnForPath(path),
		};
	},
	float<T>(
		path: KeyOrPathArray<T>,
		decimalPlaces = 2,
		fallback: string | null = null,
	): Pick<TableColumn<T, string>, "name" | "cellClassName" | "align" | "content" | "sortFn"> {
		return {
			name: pathToStr(path),
			cellClassName: "tabular-nums",
			align: "end",
			content: (row) => {
				const val = accessByPath(row, path);
				return val == null ? fallback : formatNumber(val as number, decimalPlaces);
			},
			sortFn: builtInSortFnForPath(path),
		};
	},
	integer<T>(
		path: KeyOrPathArray<T>,
		fallback: string | null = null,
	): Pick<TableColumn<T, string>, "name" | "cellClassName" | "align" | "content" | "sortFn"> {
		return {
			name: pathToStr(path),
			cellClassName: "tabular-nums",
			align: "end",
			content: (row) => {
				const val = accessByPath(row, path);
				return val == null ? fallback : formatNumber(val as number, 0);
			},
			sortFn: builtInSortFnForPath(path),
		};
	},
	percentage<T>(
		path: KeyOrPathArray<T>,
		decimalPlaces = 2,
		fallback: string | null = null,
	): Pick<TableColumn<T, string>, "name" | "cellClassName" | "align" | "content" | "sortFn"> {
		return {
			name: pathToStr(path),
			cellClassName: "tabular-nums",
			align: "end",
			content: (row) => {
				const val = accessByPath(row, path);
				return val == null ? fallback : `${formatNumber(val as number, decimalPlaces)}%`;
			},
			sortFn: builtInSortFnForPath(path),
		};
	},
	currencyAmount<T>(
		amountPath: KeyOrPathArray<T>,
		currencyPath: KeyOrPathArray<T>,
		decimalPlaces = 2,
		fallback: string | null = null,
	): Pick<TableColumn<T, string>, "name" | "cellClassName" | "align" | "content" | "sortFn"> {
		return {
			name: pathToStr(amountPath),
			cellClassName: "tabular-nums",
			align: "end",
			content: (row) => {
				const amount = accessByPath(row, amountPath);
				if (amount == null) {
					return fallback;
				}
				const currency = accessByPath(row, currencyPath);
				const formatted = formatNumber(amount as number, decimalPlaces);
				return currency ? `${formatted} ${currency}` : formatted;
			},
			sortFn: builtInSortFnForPath(amountPath),
		};
	},
	date<T>(
		path: KeyOrPathArray<T>,
	): Pick<TableColumn<T, string>, "name" | "cellClassName" | "align" | "content" | "sortFn"> {
		return {
			name: pathToStr(path),
			cellClassName: "tabular-nums",
			align: "end",
			content: (row) => {
				const val = accessByPath(row, path);
				return val ? formatDate(new Date(val as string | number)) : "-";
			},
			sortFn: (rowA, rowB) => {
				const a = accessByPath(rowA, path);
				const b = accessByPath(rowB, path);
				return builtInSort(
					a && new Date(a as string | number).getTime(),
					b && new Date(b as string | number).getTime(),
				);
			},
		};
	},
};

export const commonGroupColumns = {
	string<TGroup extends { rows: unknown[] }>(
		groupPath: KeyOrPathArray<TGroup>,
		rowPath: KeyOrPathArray<TGroup["rows"][number]>,
	): Pick<TableWithGroupsColumn<TGroup, string>, "name" | "groupContent" | "content" | "sortFn"> {
		return {
			name: pathToStr(groupPath),
			groupContent: (row) => {
				const val = accessByPath(row, groupPath);
				return val == null || typeof val !== "string" ? null : String(val);
			},
			content: (row) => {
				const val = accessByPath(row, rowPath);
				return val == null || typeof val !== "string" ? null : String(val);
			},
			sortFn: builtInCaseInsensitiveSortFnForPath(groupPath),
		};
	},
	float<TGroup extends { rows: unknown[] }>(
		groupPath: KeyOrPathArray<TGroup>,
		rowPath: KeyOrPathArray<TGroup["rows"][number]>,
		decimalPlaces = 2,
		fallback: string | null = null,
	): Pick<
		TableWithGroupsColumn<TGroup, string>,
		"name" | "cellClassName" | "groupCellClassName" | "groupContent" | "align" | "content" | "sortFn"
	> {
		return {
			name: pathToStr(groupPath),
			cellClassName: "tabular-nums",
			groupCellClassName: "tabular-nums",
			align: "end",
			groupContent: (row) => {
				const val = accessByPath(row, groupPath);
				return val == null ? fallback : formatNumber(val as number, decimalPlaces);
			},
			content: (row) => {
				const val = accessByPath(row, rowPath);
				return val == null ? fallback : formatNumber(val as number, decimalPlaces);
			},
			sortFn: builtInSortFnForPath(groupPath),
		};
	},
	integer<TGroup extends { rows: unknown[] }>(
		groupPath: KeyOrPathArray<TGroup>,
		rowPath: KeyOrPathArray<TGroup["rows"][number]>,
		fallback: string | null = null,
	): Pick<
		TableWithGroupsColumn<TGroup, string>,
		"name" | "cellClassName" | "groupCellClassName" | "groupContent" | "align" | "content" | "sortFn"
	> {
		return {
			name: pathToStr(groupPath),
			cellClassName: "tabular-nums",
			groupCellClassName: "tabular-nums",
			align: "end",
			groupContent: (row) => {
				const val = accessByPath(row, groupPath);
				return val == null ? fallback : formatNumber(val as number, 0);
			},
			content: (row) => {
				const val = accessByPath(row, rowPath);
				return val == null ? fallback : formatNumber(val as number, 0);
			},
			sortFn: builtInSortFnForPath(groupPath),
		};
	},
	percentage<TGroup extends { rows: unknown[] }>(
		groupPath: KeyOrPathArray<TGroup>,
		rowPath: KeyOrPathArray<TGroup["rows"][number]>,
		decimalPlaces = 2,
		fallback: string | null = null,
	): Pick<
		TableWithGroupsColumn<TGroup, string>,
		"name" | "cellClassName" | "groupCellClassName" | "groupContent" | "align" | "content" | "sortFn"
	> {
		return {
			name: pathToStr(groupPath),
			cellClassName: "tabular-nums",
			groupCellClassName: "tabular-nums",
			align: "end",
			groupContent: (row) => {
				const val = accessByPath(row, groupPath);
				return val == null ? fallback : `${formatNumber(val as number, decimalPlaces)}%`;
			},
			content: (row) => {
				const val = accessByPath(row, rowPath);
				return val == null ? fallback : `${formatNumber(val as number, decimalPlaces)}%`;
			},
			sortFn: builtInSortFnForPath(groupPath),
		};
	},
	date<TGroup extends { rows: unknown[] }>(
		groupPath: KeyOrPathArray<TGroup>,
		rowPath: KeyOrPathArray<TGroup["rows"][number]>,
	): Pick<
		TableWithGroupsColumn<TGroup, string>,
		"name" | "cellClassName" | "groupCellClassName" | "groupContent" | "align" | "content" | "sortFn"
	> {
		return {
			name: pathToStr(groupPath),
			cellClassName: "tabular-nums",
			groupCellClassName: "tabular-nums",
			align: "end",
			groupContent: (row) => {
				const val = accessByPath(row, groupPath);
				return val ? formatDate(new Date(val as string | number)) : "-";
			},
			content: (row) => {
				const val = accessByPath(row, rowPath);
				return val ? formatDate(new Date(val as string | number)) : "-";
			},
			sortFn: (rowA, rowB) => {
				const a = accessByPath(rowA, groupPath);
				const b = accessByPath(rowB, groupPath);
				return builtInSort(
					a && new Date(a as string | number).getTime(),
					b && new Date(b as string | number).getTime(),
				);
			},
		};
	},
};
